/**
 * React Query hooks for Knowledge Base operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { knowledgeBaseApi } from '@/lib/api';
import type { CreateKnowledgeBaseDto, UpdateKnowledgeBaseDto } from '@/lib/api/knowledge-base.api';
import type { KBDocument } from '@shared/types/knowledge-base';
import { queryKeys } from '../client';
import { snapshotIds, useResilientMutation } from '../useResilientMutation';
import {
  verifyDocumentDeleted,
  verifyDocumentUploaded,
  verifyRetryAccepted,
  type DeleteDocumentVars,
  type RetryDocumentVars,
  type UploadVars,
} from '../verifiers';
import { deriveKOStatus } from '@shared/utils/knowledge-object-status';

// ─── Knowledge Bases ──────────────────────────────────────────────────────

export function useKnowledgeBases() {
  return useQuery({
    queryKey: queryKeys.knowledgeBases,
    queryFn: () => knowledgeBaseApi.list(),
  });
}

export function useKnowledgeBase(id: string) {
  return useQuery({
    queryKey: queryKeys.knowledgeBase(id),
    queryFn: () => knowledgeBaseApi.get(id),
    enabled: !!id,
  });
}

export function useCreateKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateKnowledgeBaseDto) => knowledgeBaseApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases });
    },
  });
}

export function useUpdateKnowledgeBase(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateKnowledgeBaseDto) => knowledgeBaseApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases });
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBase(id) });
    },
  });
}

export function useDeleteKnowledgeBase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeBaseApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.knowledgeBases });
    },
  });
}

// ─── Documents ────────────────────────────────────────────────────────────

/**
 * Poll cadence for the KB document list while ingestion is in flight.
 *
 * 5s, matching the other live lists (explorer 3-5s, datasets 3-4s), because a
 * conversion runs for minutes: a 1s poll would issue ~300 requests for a single
 * 173-page PDF and buy nothing a human can perceive, while 30s would read as a
 * dead page and send users back to F5.
 */
export const KB_DOCUMENTS_POLL_MS = 5_000;

/**
 * Slow poll kept running once every document is terminal.
 *
 * Returning `false` here (what this did first) makes the tab blind: the stop decision is taken
 * from the data the tab ALREADY has, so any change made elsewhere is invisible. Measured
 * 2026-08-12: six documents sat at `failed`, so polling was off, they were retried from
 * outside the tab (a CLI call, but another operator, a second tab or the worker itself would
 * do the same), and the list stayed on `failed` until the user pressed F5. The global
 * `staleTime` of 5 minutes also neutralises `refetchOnWindowFocus` inside that window, so
 * coming back to the tab did not help either.
 *
 * 30s costs 2 requests a minute, and only while the tab is actually being looked at:
 * TanStack pauses interval refetching for a background tab by default
 * (`refetchIntervalInBackground` is false). That is a cheap price for a page whose entire job
 * is to show what the pipeline is doing.
 */
export const KB_DOCUMENTS_IDLE_POLL_MS = 30_000;

/**
 * True while a document is still moving, i.e. its *derived* status is neither
 * `complete` nor `failed`.
 *
 * It has to go through deriveKOStatus rather than compare `conversion_status`
 * to the four raw DB values: a row can be `completed` at the conversion step
 * yet still be building its L1 units, which the UI shows as "Processing - L1".
 * Comparing raw values would stop the poll while the tag still says Processing.
 */
export function hasDocumentInProgress(docs: readonly KBDocument[]): boolean {
  return docs.some((d) => {
    const { status } = deriveKOStatus(
      d.conversion_status,
      d.knowledge_unit_count ?? 0,
      d.summary_count ?? 0,
      d.extraction_count ?? 0,
      d.layers_status,
    );
    return status !== 'complete' && status !== 'failed';
  });
}

/**
 * `refetchInterval` for the KB document list. Exported so the start/stop
 * decision can be unit-tested without driving timers through a full render.
 *
 * Returns `false` once every document is terminal so an idle tab stops calling
 * the API, and resumes on its own as soon as a retry or an upload puts a row
 * back into a non-terminal state.
 */
export function kbDocumentsRefetchInterval(
  data: readonly KBDocument[] | undefined,
): number | false {
  // No data yet (first load, or an error before any success): the query is already fetching or
  // retrying on its own, so there is nothing to schedule.
  if (!Array.isArray(data)) return false;
  return hasDocumentInProgress(data) ? KB_DOCUMENTS_POLL_MS : KB_DOCUMENTS_IDLE_POLL_MS;
}

export function useKBDocuments(kbId: string) {
  return useQuery({
    queryKey: queryKeys.kbDocuments(kbId),
    queryFn: () => knowledgeBaseApi.listDocuments(kbId),
    enabled: !!kbId,
    refetchInterval: (query) => kbDocumentsRefetchInterval(query.state.data),
    // Overrides the global 5 minute staleTime. This list is a live view of a pipeline that
    // moves on its own, so "fresh for 5 minutes" is wrong twice: it suppresses the
    // refetchOnWindowFocus that would otherwise catch up on return to the tab, and it lets a
    // remount paint known-stale rows.
    staleTime: 0,
  });
}

/**
 * Upload one document.
 *
 * `submit(file)` takes the baseline from the cache the page is already showing, so a caller
 * uploading several files in a loop does not have to think about it.
 */
export function useUploadDocument(kbId: string) {
  const queryClient = useQueryClient();
  const mutation = useResilientMutation<KBDocument, UploadVars>({
    mutationFn: (vars) => knowledgeBaseApi.uploadDocument(vars.kbId, vars.file),
    verify: verifyDocumentUploaded,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
    },
  });

  const submit = (file: File) => {
    const cached = queryClient.getQueryData<KBDocument[]>(queryKeys.kbDocuments(kbId));
    return mutation.submit({
      kbId,
      file,
      knownIds: snapshotIds(cached),
      snapshotAvailable: Array.isArray(cached),
    });
  };

  return { ...mutation, submit };
}

/**
 * Import documents from another KB into `kbId` (copy with units + embeddings).
 * After success, refresh this KB's document list so the imported docs appear.
 */
export function useImportDocuments(kbId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { sourceKbId: string; documentIds?: string[]; all?: boolean }) =>
      knowledgeBaseApi.importDocuments(kbId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
    },
  });
}

/**
 * Ontology concepts grounded in a document (across explorations). Drives the
 * "Concepts in this document" panel on the knowledge-object detail page.
 */
export function useKnowledgeObjectConcepts(koId: string, enabled = true) {
  return useQuery({
    queryKey: ['knowledge-objects', koId, 'concepts'],
    queryFn: () => knowledgeBaseApi.getKnowledgeObjectConcepts(koId),
    enabled: enabled && !!koId,
  });
}

export function useDeleteDocument(kbId: string) {
  const queryClient = useQueryClient();
  const mutation = useResilientMutation<{ deletedId: string }, DeleteDocumentVars>({
    mutationFn: async (vars) => {
      await knowledgeBaseApi.deleteDocument(vars.docId);
      return { deletedId: vars.docId };
    },
    verify: verifyDocumentDeleted,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
    },
  });

  return { ...mutation, submit: (docId: string) => mutation.submit({ kbId, docId }) };
}

export function useDocumentMarkdown(docId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.kbDocumentMarkdown(docId),
    queryFn: () => knowledgeBaseApi.getDocumentMarkdown(docId),
    enabled: !!docId && enabled,
  });
}

// ─── Knowledge Units ──────────────────────────────────────────────────────

/**
 * Fetch the knowledge units (chunks) of a knowledge object. Only enabled when
 * the caller passes `enabled: true` (typically when an expandable row opens),
 * so we don't pay a request per row in the list view.
 *
 * The `kbId` is needed to invalidate the parent KO list after a re-chunk so
 * the unit-count column refreshes.
 */
export function useKnowledgeUnits(knowledgeObjectId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.knowledgeUnits(knowledgeObjectId),
    queryFn: () => knowledgeBaseApi.listKnowledgeUnits(knowledgeObjectId),
    enabled: !!knowledgeObjectId && enabled,
  });
}

/**
 * Fetch L2 (summaries) and L3 (extractions) for all units of a knowledge
 * object. Loaded alongside units when the expanded panel is open.
 *
 * Polls every 5s as long as the layers are incomplete relative to the unit
 * count so the KO detail page updates live while L2/L3 generation runs.
 */
export function useKnowledgeUnitLayers(
  knowledgeObjectId: string,
  enabled = true,
  totalUnitCount?: number,
) {
  return useQuery({
    queryKey: queryKeys.knowledgeUnitLayers(knowledgeObjectId),
    queryFn: () => knowledgeBaseApi.listKnowledgeUnitLayers(knowledgeObjectId),
    enabled: !!knowledgeObjectId && enabled,
    // Keep polling while L2 or L3 generation is still in progress.
    // We consider it "in progress" when we know the total unit count but the
    // layers haven't reached it yet (or either count is still zero with units
    // present). Stops once summaries and extractions are populated.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const n = totalUnitCount ?? 0;
      if (n === 0) return false;
      const l2Done = data.summaries.length >= n;
      const l3Done = data.extractions.length > 0;
      return l2Done && l3Done ? false : 5_000;
    },
  });
}

/**
 * Trigger a re-chunk of a knowledge object. After success we invalidate the
 * parent KB's document list (so the unit-count column rerenders) and the
 * unit list itself.
 */
export function useRechunkKnowledgeObject(kbId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (knowledgeObjectId: string) =>
      knowledgeBaseApi.rechunkKnowledgeObject(knowledgeObjectId),
    onSuccess: (_data, knowledgeObjectId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.knowledgeUnits(knowledgeObjectId),
      });
    },
  });
}

/**
 * Retry ingestion of a failed / stuck knowledge object. The server re-fires the
 * COS pipeline; we invalidate the KB document list so the row flips back to
 * "pending" and the poller tracks it to completion (or a fresh failure).
 */
export function useRetryKnowledgeObject(kbId: string) {
  const queryClient = useQueryClient();
  const mutation = useResilientMutation<{ ok: true }, RetryDocumentVars>({
    mutationFn: (vars) => knowledgeBaseApi.retryKnowledgeObject(vars.docId),
    verify: verifyRetryAccepted,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
    },
  });

  const submit = (docId: string) => {
    const cached = queryClient.getQueryData<KBDocument[]>(queryKeys.kbDocuments(kbId));
    const row = cached?.find((d) => d.id === docId);
    return mutation.submit({ kbId, docId, statusBefore: row?.conversion_status ?? null });
  };

  return { ...mutation, submit };
}

/**
 * Rename a knowledge object's display label (A15). Invalidate the KB document
 * list so the row shows the new name.
 */
export function useRenameKnowledgeObject(kbId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, displayName }: { id: string; displayName: string | null }) =>
      knowledgeBaseApi.renameKnowledgeObject(id, displayName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
    },
  });
}

/**
 * "Rename with AI" — suggest a display name from a document's own knowledge
 * units. This never persists anything, so no cache invalidation is needed;
 * the caller fills the Rename dialog input with the returned suggestion.
 */
export function useSuggestDisplayName() {
  return useMutation({
    mutationFn: (knowledgeObjectId: string) => knowledgeBaseApi.suggestDisplayName(knowledgeObjectId),
  });
}

/**
 * Trigger the L2/L3 layer pipeline (summarization + extractions) for an
 * already-chunked knowledge object. After success we invalidate the layers
 * cache so the UI refreshes with new summaries/extractions, and the parent
 * KB document list so layer counts update.
 */
export function useProcessLayers(kbId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (knowledgeObjectId: string) =>
      knowledgeBaseApi.processLayers(knowledgeObjectId),
    onSuccess: (_data, knowledgeObjectId) => {
      const refresh = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.kbDocuments(kbId) });
        queryClient.invalidateQueries({
          queryKey: queryKeys.knowledgeUnitLayers(knowledgeObjectId),
        });
      };
      // Twice, and the second one is the point. Since the 2026-08-24 cutover the request only QUEUES the
      // work (202), so refreshing solely on success shows the state from before anything happened, and the
      // button looks broken. The delayed refresh is when `layers_status` has become 'processing', which is
      // the first visible sign that the worker took it.
      refresh();
      setTimeout(refresh, 6000);
    },
  });
}

// Made with Bob

